import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Calendar,
  Users,
  FileText,
  ArrowLeft,
  Edit,
  Lock,
  Loader2,
  ListChecks
} from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';

import SourcingRfpService from '../../services/sourcingRFPService';
import VendorService from '../../services/vendorService';

//////////////////////////////////////////////////////////////
// STATUS BADGE
//////////////////////////////////////////////////////////////

const StatusBadge = ({ status }) => {
  const config = {
    DRAFT: { bg: '#f3f4f6', color: '#6b7280' },
    PUBLISHED: { bg: '#dbeafe', color: '#2563eb' },
    EVALUATION: { bg: '#fef3c7', color: '#d97706' },
    AWARDED: { bg: '#d1fae5', color: '#059669' },
    CANCELLED: { bg: '#fee2e2', color: '#dc2626' },
  };

  const style = config[status] || config.DRAFT;

  return (
    <span
      style={{
        background: style.bg,
        color: style.color,
        padding: '6px 14px',
        borderRadius: 999,
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      {status}
    </span>
  );
};

//////////////////////////////////////////////////////////////
// BIG CARD
//////////////////////////////////////////////////////////////

const BigCard = ({ title, icon: Icon, children }) => (
  <div
    style={{
      background: '#fff',
      borderRadius: 14,
      padding: 26,
      border: '1px solid #e5e7eb',
      minHeight: 200,
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: 14,
        fontWeight: 600,
        fontSize: 16,
      }}
    >
      <Icon size={18} style={{ marginRight: 8 }} />
      {title}
    </div>

    {children}
  </div>
);

//////////////////////////////////////////////////////////////
// META CARD
//////////////////////////////////////////////////////////////

const MetaCard = ({ label, value }) => (
  <div
    style={{
      background: '#f9fafb',
      borderRadius: 10,
      padding: '12px 16px',
      minWidth: 140,
    }}
  >
    <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
    <div style={{ fontWeight: 600 }}>{value}</div>
  </div>
);

//////////////////////////////////////////////////////////////
// MAIN VIEW
//////////////////////////////////////////////////////////////

const ViewRfp = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { error: showError, toasts, removeToast } = useToast();

  const [rfp, setRfp] = useState(null);
  const [vendorsMap, setVendorsMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [rfpRes, vendorRes] = await Promise.all([
        SourcingRfpService.getRfpById(id),
        VendorService.getVendors({ limit: 1000 }),
      ]);

      const rfpData = rfpRes.data;
      const vendorList = vendorRes?.data?.vendors || vendorRes?.data || [];
      console.log("RFP API Response:", rfpRes);
      const map = {};
      vendorList.forEach(v => {
        map[v._id] = v.name || v.companyName || v.vendorName;
      });

      setVendorsMap(map);
      setRfp(rfpData);
    } catch (err) {
      showError(err.message || 'Failed to load RFP');
      navigate('/sourcing/rfps');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader title="View RFP" description="Loading..." />
        <div style={{ textAlign: 'center', padding: 80 }}>
          <Loader2 size={40} className="spinning" />
        </div>
      </>
    );
  }

  if (!rfp) return null;

  const headerActions = [
    {
      label: 'Back',
      icon: <ArrowLeft size={18} />,
      variant: 'outline',
      onClick: () => navigate('/sourcing/rfps'),
    },
    {
      label: 'Edit',
      icon: <Edit size={18} />,
      variant: 'primary',
      onClick: () => navigate(`/sourcing/rfps/edit/${id}`),
      disabled: rfp?.status !== 'DRAFT',
    },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="RFP Details"
        description={`RFP #${rfp.rfpNumber || '--'}`}
        actions={headerActions}
      />

      <div style={{ maxWidth: 1300, margin: '0 auto', padding: 32 }}>
        {/* HERO SECTION */}
        <div style={{ background: '#fff', borderRadius: 14, padding: 28, border: '1px solid #e5e7eb', marginBottom: 26 }}>
          <h1 style={{ fontSize: 30, fontWeight: 700 }}>{rfp.title}</h1>
          <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
            <StatusBadge status={rfp.status} />
            {rfp.status !== 'DRAFT' && (
              <div style={{ color: '#92400e', display: 'flex', alignItems: 'center', fontSize: 14 }}>
                <Lock size={16} style={{ marginRight: 6 }} /> Locked
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 18 }}>
            <MetaCard label="Created" value={rfp.createdAt ? new Date(rfp.createdAt).toLocaleDateString() : '--'} />
            <MetaCard label="Deadline" value={rfp.responseDeadline ? new Date(rfp.responseDeadline).toLocaleDateString() : '--'} />
          </div>
        </div>

        {/* LINE ITEMS SECTION (Full Width) */}
        <div style={{ marginBottom: 24 }}>
          <BigCard title="Line Items & Requirements" icon={ListChecks}>
            <div style={{ width: '100%', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f3f4f6' }}>
                    <th style={{ padding: '12px 0', color: '#6b7280', fontSize: 13, fontWeight: 600 }}>#</th>
                    <th style={{ padding: '12px 0', color: '#6b7280', fontSize: 13, fontWeight: 600 }}>Description</th>
                    <th style={{ padding: '12px 0', color: '#6b7280', fontSize: 13, fontWeight: 600 }}>Qty / Requirement</th>
                  </tr>
                </thead>
                <tbody>
                  {rfp.lineItems?.length > 0 ? (
                    rfp.lineItems.map((item, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '16px 0', fontSize: 14, color: '#9ca3af', width: 40 }}>{index + 1}</td>
                        <td style={{ padding: '16px 0', fontSize: 14, fontWeight: 500 }}>{item.description}</td>
                        <td style={{ padding: '16px 0', fontSize: 14, color: '#4b5563' }}>{item.requirement || '—'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" style={{ padding: '24px 0', textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>
                        No specific line items defined.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </BigCard>
        </div>

        {/* SCOPE & VENDORS GRID */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <BigCard title="Scope of Work" icon={FileText}>
            <div style={{ lineHeight: 1.7, whiteSpace: 'pre-wrap', color: '#374151' }}>
              {rfp.scope || 'No scope provided.'}
            </div>
          </BigCard>

          <BigCard title="Invited Vendors" icon={Users}>
            {rfp.invitedVendors?.length ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                {rfp.invitedVendors.map(v => {
                  const vendorId = typeof v === 'object' ? v._id : v;
                  const vendorName = typeof v === 'object' 
                    ? (v.name || v.companyName) 
                    : (vendorsMap[v] || 'Vendor');

                  return (
                    <span key={vendorId} style={{ padding: '8px 14px', background: '#eef2ff', borderRadius: 999, fontWeight: 600, color: '#4338ca', fontSize: 13 }}>
                      {vendorName}
                    </span>
                  );
                })}
              </div>
            ) : (
              <div style={{ color: '#6b7280' }}>No vendors invited.</div>
            )}
          </BigCard>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default ViewRfp;